import { lazy, Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageLoader } from '@/components/common/PageLoader';

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// Core Pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const DocumentationPage = lazy(() => import('@/pages/DocumentationPage'));
const FundManagementPage = lazy(() => import('@/pages/FundManagementPage'));
const HelpPage = lazy(() => import('@/pages/HelpPage'));
const PortfolioViewPage = lazy(() => import('@/pages/PortfolioViewPage'));
const BenchmarkManagementPage = lazy(() => import('@/pages/BenchmarkManagementPage'));
const FundBenchmarkAnalysisPage = lazy(() => import('@/pages/FundBenchmarkAnalysisPage'));
const UsagePage = lazy(() => import('@/pages/UsagePage'));

// Fund Pages
const CustomBenchmarkPage = lazy(() => import('@/pages/fund/CustomBenchmarkPage'));
const StatisticalMetricsPage = lazy(() => import('@/pages/fund/StatisticalMetricsPage'));

// Portfolio Optimization Pages
const PortfolioOptimization = lazy(() => import('@/pages/optimization').then(m => ({ default: m.PortfolioOptimization })));
const RebalancingPage = lazy(() => import('@/pages/optimization').then(m => ({ default: m.RebalancingPage })));
const OptimizationRunConsolePage = lazy(() => import('@/pages/optimization/OptimizationRunConsolePage'));

// Fee Management Pages
const FeeManagementPage = lazy(() => import('@/pages/fee-management').then(m => ({ default: m.FeeManagementPage })));
const FeeConfigurationPage = lazy(() => import('@/pages/fee-management/FeeConfigurationPage'));
const FeeChargesPage = lazy(() => import('@/pages/fee-management/FeeChargesPage'));
const FeeInvoicingPage = lazy(() => import('@/pages/fee-management/FeeInvoicingPage'));

export default function MiscRoutes() {
  return (
    <>
      {/* Home */}
      <Route path="/" element={<ProtectedRoute><MainLayout><LazyPage><HomePage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><MainLayout><LazyPage><DashboardPage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Portfolio View */}
      <Route path="/portfolio-view" element={<ProtectedRoute><MainLayout><LazyPage><PortfolioViewPage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Main View */}
      <Route path="/main-view/fund-benchmark" element={<ProtectedRoute><MainLayout><LazyPage><FundBenchmarkAnalysisPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/main-view/tracking-metrics" element={<ProtectedRoute><Navigate to="/main-view/fund-benchmark" replace /></ProtectedRoute>} />
      <Route path="/main-view/usage" element={<ProtectedRoute><MainLayout><LazyPage><UsagePage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Fund Management */}
      <Route path="/fund/statistical-metrics" element={<ProtectedRoute><MainLayout><LazyPage><StatisticalMetricsPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fund/benchmark/custom-benchmark" element={<ProtectedRoute><MainLayout><LazyPage><CustomBenchmarkPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fund/*" element={<ProtectedRoute><MainLayout><LazyPage><FundManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Admin & Data Management */}
      <Route path="/admin/benchmarks" element={<ProtectedRoute><MainLayout><LazyPage><BenchmarkManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Help & Documentation */}
      <Route path="/help" element={<ProtectedRoute><MainLayout><LazyPage><HelpPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/documentation" element={<ProtectedRoute><MainLayout><LazyPage><DocumentationPage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Portfolio Optimization */}
      <Route path="/optimization" element={<ProtectedRoute><MainLayout><LazyPage><PortfolioOptimization /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/dashboard" element={<ProtectedRoute><MainLayout><LazyPage><PortfolioOptimization /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/rebalancing" element={<ProtectedRoute><MainLayout><LazyPage><RebalancingPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/run-console" element={<ProtectedRoute><MainLayout><LazyPage><OptimizationRunConsolePage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/quick/*" element={<ProtectedRoute><MainLayout><LazyPage><PortfolioOptimization /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/full" element={<ProtectedRoute><MainLayout><LazyPage><PortfolioOptimization /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/universes" element={<ProtectedRoute><MainLayout><LazyPage><PortfolioOptimization /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/history" element={<ProtectedRoute><MainLayout><LazyPage><PortfolioOptimization /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Fee Management */}
      <Route path="/fee-management" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/structures" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/assignments" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/accruals" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/commissions" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/exchange-fees" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/tax-withholding" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/analytics" element={<ProtectedRoute><MainLayout><LazyPage><FeeManagementPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/configuration" element={<ProtectedRoute><MainLayout><LazyPage><FeeConfigurationPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/charges" element={<ProtectedRoute><MainLayout><LazyPage><FeeChargesPage /></LazyPage></MainLayout></ProtectedRoute>} />
      <Route path="/fee-management/invoicing" element={<ProtectedRoute><MainLayout><LazyPage><FeeInvoicingPage /></LazyPage></MainLayout></ProtectedRoute>} />
    </>
  );
}
